import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useBrandKit } from '../../contexts/BrandKitContext';
import { assetsApi } from '../../lib/assetsApi';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiLoader, FiAlertTriangle, FiFileText, FiType, FiDroplet } = FiIcons;

// Locked brand assets that cannot be changed
const LOCKED_COLORS = {
  primary: '#03245E',
  accent: '#E2C164',
  background: '#F3F7FA',
  secondary: '#D7B740',
  highlight: '#F7EFE2'
};

const LOCKED_FONTS = {
  titles: 'Anton',
  body: 'Helvetica World',
  subtitle: 'Montserrat',
  heading: 'League Spartan',
  highlight: 'Gistesy'
};

const LockedBrandProcessor = ({ documentFile, onProcessingComplete }) => {
  const { currentBrandKit } = useBrandKit();
  const [processingStatus, setProcessingStatus] = useState('idle'); // idle, scanning, applying, complete
  const [progress, setProgress] = useState(0);
  const [processingResults, setProcessingResults] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [watermarkUrl, setWatermarkUrl] = useState(null);

  // Load brand assets
  useEffect(() => {
    const loadBrandAssets = async () => {
      if (!currentBrandKit) return;

      try {
        // Load logo
        if (currentBrandKit.logos?.length > 0) {
          const selectedLogo = currentBrandKit.logos.find(
            logo => logo.id === currentBrandKit.selected_logo_id
          ) || currentBrandKit.logos[0];

          if (selectedLogo?.dropbox_path) {
            const url = await assetsApi.getAssetUrl(selectedLogo.dropbox_path);
            setLogoUrl(url);
          }
        }

        // Load watermark
        if (currentBrandKit.watermarks?.length > 0) {
          const selectedWatermark = currentBrandKit.watermarks.find(
            watermark => watermark.id === currentBrandKit.selected_watermark_id
          ) || currentBrandKit.watermarks[0];

          if (selectedWatermark?.dropbox_path) {
            const url = await assetsApi.getAssetUrl(selectedWatermark.dropbox_path);
            setWatermarkUrl(url);
          }
        }
      } catch (error) {
        console.warn('Failed to load brand assets:', error);
      }
    };

    loadBrandAssets();
  }, [currentBrandKit]);

  // Process document with locked brand assets
  const processDocument = async () => {
    if (!documentFile || !currentBrandKit) return;

    setProcessingStatus('scanning');
    setProgress(0);

    // Simulate document scanning
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setProgress(i);
    }

    setProcessingStatus('applying');
    setProgress(0);

    // Simulate applying brand styling
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 150));
      setProgress(i);
    }

    // Mock processing results
    const results = {
      documentName: documentFile.name,
      pagesProcessed: 12,
      fontsApplied: Object.values(LOCKED_FONTS),
      colorsApplied: Object.keys(LOCKED_COLORS),
      logoApplied: !!logoUrl,
      watermarkApplied: !!watermarkUrl,
      processingTime: '3.2s',
      fileSize: `${(documentFile.size / 1024 / 1024).toFixed(2)} MB`
    };

    setProcessingResults(results);
    setProcessingStatus('complete');

    if (onProcessingComplete) {
      onProcessingComplete(results);
    }
  };

  // Start processing when document and brand kit are available
  useEffect(() => {
    if (documentFile && currentBrandKit && processingStatus === 'idle') {
      processDocument();
    }
  }, [documentFile, currentBrandKit, processingStatus]);

  if (!documentFile) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <SafeIcon icon={FiFileText} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Document Selected</h3>
        <p className="text-gray-600">Please upload a document to begin processing</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Document Processing</h2>
        <div className="flex items-center text-sm text-gray-500">
          <span>Applying locked brand assets</span>
        </div>
      </div>

      {processingStatus !== 'complete' && (
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>
              {processingStatus === 'scanning' 
                ? 'Scanning document structure...' 
                : 'Applying brand styling...'}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <motion.div 
              className="bg-blue-600 h-2.5 rounded-full" 
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      )}

      {processingStatus === 'scanning' && (
        <div className="space-y-4">
          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
            <SafeIcon icon={FiLoader} className="w-5 h-5 text-blue-600 mr-3 animate-spin" />
            <span className="text-blue-800">Analyzing document structure and content</span>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <SafeIcon icon={FiFileText} className="w-6 h-6 text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Pages</p>
              <p className="text-lg font-semibold text-gray-900">--</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <SafeIcon icon={FiType} className="w-6 h-6 text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Fonts</p>
              <p className="text-lg font-semibold text-gray-900">--</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <SafeIcon icon={FiDroplet} className="w-6 h-6 text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Colors</p>
              <p className="text-lg font-semibold text-gray-900">--</p>
            </div>
          </div>
        </div>
      )}

      {processingStatus === 'applying' && (
        <div className="space-y-4">
          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
            <SafeIcon icon={FiLoader} className="w-5 h-5 text-blue-600 mr-3 animate-spin" />
            <span className="text-blue-800">Applying locked brand styling</span>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Updating fonts with locked brand fonts</span>
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Applying locked brand colors</span>
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Adding locked logo</span>
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Adding locked watermark</span>
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        </div>
      )}

      {processingStatus === 'complete' && processingResults && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="text-center">
            <div className="inline-block bg-green-100 p-3 rounded-full mb-4">
              <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Document Successfully Processed!</h3>
            <p className="text-gray-600">
              Your document has been branded with locked assets from {currentBrandKit?.name || 'your brand kit'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-green-800 font-medium">Pages Processed</div>
              <div className="text-2xl font-bold text-green-900">{processingResults.pagesProcessed}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-green-800 font-medium">Processing Time</div>
              <div className="text-2xl font-bold text-green-900">{processingResults.processingTime}</div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Applied Brand Assets</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-green-800">Locked Fonts</span>
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-green-800">Locked Colors</span>
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-green-800">
                    {processingResults.logoApplied ? 'Locked Logo' : 'No Logo Available'}
                  </span>
                  <SafeIcon 
                    icon={processingResults.logoApplied ? FiCheck : FiAlertTriangle} 
                    className={`w-5 h-5 ${processingResults.logoApplied ? 'text-green-600' : 'text-yellow-600'}`} 
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-green-800">
                    {processingResults.watermarkApplied ? 'Locked Watermark' : 'No Watermark Available'}
                  </span>
                  <SafeIcon 
                    icon={processingResults.watermarkApplied ? FiCheck : FiAlertTriangle} 
                    className={`w-5 h-5 ${processingResults.watermarkApplied ? 'text-green-600' : 'text-yellow-600'}`} 
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Locked Brand Colors</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(LOCKED_COLORS).map(([name, color]) => (
                  <div key={name} className="flex items-center">
                    <div 
                      className="w-6 h-6 rounded border border-gray-300 mr-2" 
                      style={{ backgroundColor: color }}
                    ></div>
                    <span className="text-sm text-gray-600 capitalize">{name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Locked Typography</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(LOCKED_FONTS).map(([role, font]) => (
                  <div key={role} className="p-2 bg-gray-50 rounded">
                    <p className="text-sm text-gray-600 capitalize">{role}</p>
                    <p className="font-medium text-gray-900">{font}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default LockedBrandProcessor;